import { useEffect, useState } from "react";
import { api, formatErr, inr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Landmark, Plus, Pencil, Trash2, FileDown, Printer } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }
const emptyAcct = { name: "", bank_name: "", account_number: "", opening_balance: 0 };

export default function BankBook() {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState("");
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const pg = usePagination(data?.rows || [], data);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyAcct);
  const [editId, setEditId] = useState(null);
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/organisation").then(r => setOrg((r.data || [])[0] || null)).catch(() => {}); }, []);
  const exportPdf = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API}/reports/bank-book/pdf?from_date=${from}&to_date=${to}&bank_account_id=${selected}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "bank_book.pdf"; a.click();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const loadAccounts = () => api.get("/bank-accounts").then(r => {
    setAccounts(r.data);
    if (!selected && r.data.length) setSelected(r.data[0].id);
  }).catch(e => toast.error(formatErr(e)));
  useEffect(() => { loadAccounts(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!selected) return;
    api.get(`/reports/bank-book?from_date=${from}&to_date=${to}&bank_account_id=${selected}`)
      .then(r => setData(r.data)).catch(e => toast.error(formatErr(e)));
  }, [selected, from, to]);

  const saveAcct = async () => {
    try {
      const payload = { ...f, opening_balance: Number(f.opening_balance) };
      if (editId) await api.patch(`/bank-accounts/${editId}`, payload);
      else await api.post("/bank-accounts", payload);
      toast.success(editId ? "Updated" : "Added");
      setF(emptyAcct); setEditId(null); setOpen(false); loadAccounts();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const editAcct = (a) => { setF(a); setEditId(a.id); setOpen(true); };
  const delAcct = async (id) => {
    if (!confirm("Delete this bank account?")) return;
    try { await api.delete(`/bank-accounts/${id}`); toast.success("Deleted"); loadAccounts(); if (selected === id) setSelected(""); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8 print:hidden">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Landmark className="w-3 h-3"/> Accounts · Multi-bank deposits & withdrawals</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Bank Book</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(emptyAcct); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-bank-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
              <Plus className="w-4 h-4 mr-1"/> New Bank Account
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm bg-card border-border max-w-md">
            <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Bank Account</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Nickname</Label><Input data-testid="bank-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Bank</Label><Input data-testid="bank-bank" value={f.bank_name} onChange={(e) => setF(x => ({ ...x, bank_name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Account Number</Label><Input data-testid="bank-acct" value={f.account_number || ""} onChange={(e) => setF(x => ({ ...x, account_number: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Opening Balance ₹</Label><Input data-testid="bank-opening" type="number" value={f.opening_balance} onChange={(e) => setF(x => ({ ...x, opening_balance: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
              <Button data-testid="save-bank-btn" onClick={saveAcct} disabled={!f.name || !f.bank_name} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-end gap-3 mb-4 flex-wrap">
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Account</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="select-bank" className="mt-1 rounded-sm bg-secondary border-border w-64"><SelectValue placeholder="Pick account"/></SelectTrigger>
            <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name} · {a.bank_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">From</Label><Input data-testid="from-date" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/></div>
        <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">To</Label><Input data-testid="to-date" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/></div>
        <Button data-testid="export-pdf-bankbook" size="sm" variant="outline" onClick={exportPdf} disabled={!selected} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> PDF</Button>
        <Button data-testid="print-bankbook" size="sm" variant="outline" onClick={() => window.print()} disabled={!data} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
        {selected && (
          <div className="flex gap-1 ml-auto">
            <Button size="sm" variant="ghost" onClick={() => editAcct(accounts.find(a => a.id === selected))} className="h-9 text-xs"><Pencil className="w-3 h-3 mr-1"/> Edit</Button>
            <Button size="sm" variant="ghost" onClick={() => delAcct(selected)} className="h-9 text-xs text-destructive"><Trash2 className="w-3 h-3 mr-1"/> Delete</Button>
          </div>
        )}
      </div>

      {accounts.length === 0 && (
        <div className="border border-primary/40 bg-primary/5 p-6 rounded-sm text-center text-sm text-muted-foreground">
          No bank accounts yet — add one using the <span className="text-primary font-semibold">New Bank Account</span> button.
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Opening</div><div className="font-display font-black text-xl mt-1">{inr(data.opening_balance)}</div></div>
            <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Deposits</div><div className="font-display font-black text-xl text-green-500 mt-1">{inr(data.total_dr)}</div></div>
            <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Withdrawals</div><div className="font-display font-black text-xl text-destructive mt-1">{inr(data.total_cr)}</div></div>
            <div className="border border-primary/40 bg-primary/5 rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-primary">Closing</div><div className="font-display font-black text-xl text-primary mt-1" data-testid="bank-closing">{inr(data.closing_balance)}</div></div>
          </div>
          <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Date</th><th className="text-left p-3">Ref #</th>
                  <th className="text-left p-3">Particulars</th>
                  <th className="text-right p-3">Deposit</th><th className="text-right p-3">Withdrawal</th>
                  <th className="text-right p-3">Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No bank movements in this range</td></tr>}
                {pg.pageItems.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-3 font-mono text-xs">{r.date}</td>
                    <td className="p-3 font-mono text-xs text-primary uppercase">{r.ref}</td>
                    <td className="p-3 text-xs">{r.particulars}<div className="text-muted-foreground italic">{r.narration}</div></td>
                    <td className="p-3 text-right font-mono text-xs text-green-500">{r.dr ? inr(r.dr) : "-"}</td>
                    <td className="p-3 text-right font-mono text-xs text-destructive">{r.cr ? inr(r.cr) : "-"}</td>
                    <td className="p-3 text-right font-mono">{inr(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <PaginationBar pg={pg}/>
          </div>
        </>
      )}

      {data && (
        <div className="hidden print:block print:bg-white print:text-black" id="print-bankbook">
          <div className="p-4">
            <div className="text-xl font-bold uppercase">{org?.business_name || "SynqMoto ERP Workshop"}</div>
            <div className="text-[11px] uppercase">Establishment: {org?.establishment_name || org?.branch_name || org?.business_name || "—"}</div>
            <div className="text-lg font-bold mt-2">Bank Book</div>
            <div className="text-[11px] mb-2">Account: {data.account?.name} · {data.account?.bank_name} ({data.account?.account_number || "—"}) · Period: {from} to {to} · {data.rows.length} rows</div>
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr>
                  <th className="border border-black p-1 text-left">Date</th>
                  <th className="border border-black p-1 text-left">Ref #</th>
                  <th className="border border-black p-1 text-left">Particulars</th>
                  <th className="border border-black p-1 text-right">Deposit</th>
                  <th className="border border-black p-1 text-right">Withdrawal</th>
                  <th className="border border-black p-1 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="font-bold">
                  <td className="border border-black p-1" colSpan={3}>Opening Balance</td>
                  <td className="border border-black p-1 text-right">{inr(data.opening_balance)}</td>
                  <td className="border border-black p-1 text-right">—</td>
                  <td className="border border-black p-1 text-right">{inr(data.opening_balance)}</td>
                </tr>
                {data.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="border border-black p-1">{r.date}</td>
                    <td className="border border-black p-1">{r.ref}</td>
                    <td className="border border-black p-1">{r.particulars}{r.narration ? ` — ${r.narration}` : ""}</td>
                    <td className="border border-black p-1 text-right">{r.dr ? inr(r.dr) : "—"}</td>
                    <td className="border border-black p-1 text-right">{r.cr ? inr(r.cr) : "—"}</td>
                    <td className="border border-black p-1 text-right">{inr(r.balance)}</td>
                  </tr>
                ))}
                <tr className="font-bold">
                  <td className="border border-black p-1" colSpan={3}>TOTALS</td>
                  <td className="border border-black p-1 text-right">{inr(data.total_dr)}</td>
                  <td className="border border-black p-1 text-right">{inr(data.total_cr)}</td>
                  <td className="border border-black p-1 text-right"></td>
                </tr>
                <tr className="font-bold">
                  <td className="border border-black p-1" colSpan={3}>Closing Balance</td>
                  <td className="border border-black p-1 text-right"></td>
                  <td className="border border-black p-1 text-right"></td>
                  <td className="border border-black p-1 text-right">{inr(data.closing_balance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
